import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Calendar } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEvents } from '@/hooks/useEvents';
import { useT } from '@/i18n/LanguageContext';

export default function EventSwitcher() {
  const t = useT();
  const navigate = useNavigate();
  const { data: events } = useEvents();
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const setSelectedEventId = useAuthStore((s) => s.setSelectedEventId);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedEvent = events?.find((e) => e.id === selectedEventId);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-[10px] bg-black/[0.03] border border-reevol-border text-left hover:bg-black/[0.06] transition-colors"
      >
        <Calendar size={14} className="text-reevol-cyan flex-shrink-0" />
        <span className="text-xs font-medium text-reevol-text truncate flex-1" title={selectedEvent?.name || ''}>
          {selectedEvent?.name || t.nav.selectEvent}
        </span>
        <ChevronDown size={12} className="text-reevol-text-muted flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-reevol-border rounded-xl shadow-lg z-50 py-1 max-h-60 overflow-y-auto min-w-[280px]">
          {events?.map((event) => (
            <button
              key={event.id}
              onClick={() => {
                setSelectedEventId(event.id);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-black/[0.04] transition-colors ${
                event.id === selectedEventId ? 'bg-reevol-blue/10 text-reevol-blue font-medium' : 'text-reevol-text-secondary'
              }`}
            >
              <p className="font-medium">{event.name}</p>
              <p className="text-[10px] text-reevol-text-muted mt-0.5">
                {event.checksLimit - event.checksUsed} {t.common.checksLeft}
              </p>
            </button>
          ))}
          <div className="border-t border-reevol-border/50 mt-1 pt-1">
            <button
              onClick={() => { setOpen(false); navigate('/events'); }}
              className="w-full text-left px-3 py-2 text-xs text-reevol-cyan font-medium hover:bg-black/[0.04] transition-colors"
            >
              {t.nav.manageEvents}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
